import React, { useState } from 'react';
import { Modal, Input, Button, message, Typography, Space } from 'antd';
import { MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text, Paragraph } = Typography;

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const VerificationContainer = styled.div`
  text-align: center;
  padding: 24px;
  color: ${COLORS.textPrimary};
  
  h4.ant-typography {
    color: ${COLORS.textPrimary} !important;
  }
  
  div.ant-typography {
    color: ${COLORS.textSecondary} !important;
  }
  
  strong {
    color: ${COLORS.accent} !important;
  }
`;

const InstructionsContainer = styled.div`
  text-align: left;
  margin: 24px 0;
  padding: 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  border-left: 4px solid ${COLORS.accent};
  color: ${COLORS.textSecondary};
  
  h5.ant-typography {
    color: ${COLORS.textPrimary} !important;
    margin-bottom: 8px;
  }
  
  ol {
    color: ${COLORS.textSecondary};
  }
  
  li {
    margin-bottom: 4px;
  }
`;

const StyledInput = styled(Input)`
  background-color: ${COLORS.bgPrimary} !important;
  border-color: ${COLORS.border} !important;
  color: ${COLORS.textPrimary} !important;
  
  &:hover, &:focus {
    border-color: ${COLORS.accent} !important;
  }
  
  &::placeholder {
    color: #64748B;
  }
  
  .anticon {
    color: ${COLORS.textSecondary};
  }
`;

interface EmailCodeVerificationProps {
  visible: boolean;
  onVerify: (code: string) => Promise<void>;
  onCancel?: () => void;
  email?: string;
}

const EMAIL_CODE_LENGTH = 10;
const EMAIL_CODE_PATTERN = /^[A-Z0-9]{10}$/;

const EmailCodeVerification: React.FC<EmailCodeVerificationProps> = ({
  visible,
  onVerify,
  onCancel,
  email,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const normalizedCode = code.trim().toUpperCase();

  const handleVerify = async () => {
    if (!EMAIL_CODE_PATTERN.test(normalizedCode)) {
      message.error('Please enter a valid 10-character verification code');
      return;
    }

    setLoading(true);
    try {
      await onVerify(normalizedCode);
      message.success('Device verified successfully!');
      setCode('');
    } catch (error: any) {
      message.error(error.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MailOutlined style={{ color: COLORS.accent }} />
          <span style={{ color: COLORS.textPrimary }}>Device Verification Required</span>
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      closable={!loading}
      maskClosable={false}
      bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      className="dark-modal"
    >
      <VerificationContainer>
        <Title level={4}>Check Your Email</Title>
        <Paragraph style={{ color: COLORS.textSecondary }}>
          We've sent a verification code to {email ? <strong>{email}</strong> : 'your email address'}.
          Please enter the code below to verify this device.
        </Paragraph>

        <InstructionsContainer>
          <Title level={5}>Instructions:</Title>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>Check your email inbox (and spam folder if needed)</li>
            <li>Look for an email from Switch Wallet</li>
            <li>Find the 10-character verification code</li>
            <li>Enter the code below</li>
          </ol>
        </InstructionsContainer>

        <div style={{ marginTop: 32 }}>
          <StyledInput
            size="large"
            placeholder="Enter 10-character code"
            maxLength={EMAIL_CODE_LENGTH}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
            onPressEnter={handleVerify}
            style={{ textAlign: 'center', fontSize: 18, letterSpacing: 4, textTransform: 'uppercase' }}
            prefix={<MailOutlined />}
          />
          <Button
            type="primary"
            size="large"
            block
            icon={<CheckCircleOutlined />}
            onClick={handleVerify}
            loading={loading}
            disabled={!EMAIL_CODE_PATTERN.test(normalizedCode)}
            style={{ 
              marginTop: 16,
              background: COLORS.accent,
              borderColor: COLORS.accent,
              color: COLORS.bgPrimary,
              fontWeight: 600
            }}
          >
            Verify Device
          </Button>
        </div>

        <div style={{ marginTop: 24 }}>
          <Text style={{ fontSize: 12, color: COLORS.textSecondary }}>
            Didn't receive the code? Check your spam folder or try logging in again.
          </Text>
        </div>
      </VerificationContainer>
    </Modal>
  );
};

export default EmailCodeVerification;

